import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concat, concatMap, EMPTY, exhaustMap, filter, from, map, merge, of, switchMap, takeUntil, tap, timer, withLatestFrom } from "rxjs";
import { Store } from "@ngrx/store";
import { CallActions } from "./call.actions";
import { callFeature } from "./call.reducer";
import { CallSignalingGateway } from "../data-access/call-signaling.gateway";
import { AppDialogService } from "../../../shared/services/app-dialog.service";
import { TranslateService } from "@ngx-translate/core";
import { CallMediaCommandService } from "../data-access/call-media-command.service";
import { CallSnapshotApiService } from "../data-access/call-snapshot-api.service";
import { SessionFacade } from "../../authentication";
import { CallRecordingService } from "../data-access/call-recording.service";
import { MatDialog } from "@angular/material/dialog";
import { RecordingResultDialogComponent } from "../components/recording-result-dialog/recording-result-dialog.component";

@Injectable()
export class CallEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly signaling = inject(CallSignalingGateway);
    private readonly dialog = inject(AppDialogService);
    private readonly translate = inject(TranslateService);
    private readonly mediaCommands = inject(CallMediaCommandService);
    private readonly snapshots = inject(CallSnapshotApiService);
    private readonly session = inject(SessionFacade);
    private readonly recording = inject(CallRecordingService);
    private readonly materialDialog = inject(MatDialog);
    readonly closeCallDialogs$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.endRequested, CallActions.remoteEndReceived, CallActions.callEnded, CallActions.connectionFailed),
        tap(() => {
            for (const dialog of [...this.materialDialog.openDialogs]) {
                if (dialog.id.startsWith("call-dialog-")) dialog.close();
            }
        }),
    ), { dispatch: false });

    readonly demoInvitation$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.demoIncomingRequested),
        map(() => {
            const id = crypto.randomUUID();
            const caller = { username: "maya", clientId: "northwind", companyId: "northwind", mobileUser: false, name: "Maya Chen", userId: "maya", profilePicture: "" };
            const callee = { username: "demo-user", clientId: "gemvision", companyId: "gemvision", mobileUser: false, name: "Demo User", userId: "demo-user", profilePicture: "" };
            return CallActions.invitationReceived({ invitation: {
                source: "demo",
                id,
                audioOnly: false,
                caller,
                callee,
                participant: { id: caller.userId, name: caller.name, organization: "Northwind Operations" },
            } });
        }),
    ));

    readonly incoming$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.invitationReceived),
        tap(({ invitation }) => this.signaling.incoming(invitation)),
    ), { dispatch: false });

    readonly outgoing$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.outgoingRequested),
        exhaustMap(({ invitation }) => this.signaling.call(invitation).pipe(
            map((session) => CallActions.connectionEstablished({ session })),
            catchError(() => of(CallActions.connectionFailed({ reason: "connection-failed" }))),
            takeUntil(this.actions$.pipe(ofType(CallActions.outgoingCancelRequested, CallActions.remoteEndReceived, CallActions.callEnded))),
        )),
    ));

    readonly cancelOutgoing$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.outgoingCancelRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([, state]) => state.status === "connecting"
            ? this.signaling.cancel(state.invitation).pipe(map(() => CallActions.callEnded()))
            : of(CallActions.callEnded())),
    ));

    readonly accept$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.acceptRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([_action, state]) => {
            if (state.status !== "connecting") {
                return of(CallActions.connectionFailed({ reason: "connection-failed" }));
            }
            return this.signaling.connect(state.invitation).pipe(
                map((session) => CallActions.connectionEstablished({ session })),
                catchError(() => of(CallActions.connectionFailed({ reason: "connection-failed" }))),
                takeUntil(this.actions$.pipe(ofType(CallActions.outgoingCancelRequested, CallActions.remoteEndReceived, CallActions.callEnded))),
            );
        }),
    ));

    readonly reject$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.rejectRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([_action, state]) => state.status === "incoming"
            ? this.signaling.reject(state.invitation).pipe(map(() => CallActions.callEnded()))
            : of(CallActions.callEnded())),
    ));

    readonly end$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.endRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([_action, state]) => state.status === "ending" ? this.signaling.end(state.session).pipe(
            map(() => CallActions.callEnded()),
            catchError(() => of(CallActions.callEnded())),
        ) : of(CallActions.callEnded())),
    ));

    readonly remoteEnd$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.remoteEndReceived),
        map(() => {
            this.signaling.remoteEnded();
            return CallActions.callEnded();
        }),
    ));

    readonly failedConnectionCleanup$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.connectionFailed),
        tap(() => this.signaling.connectionFailed()),
    ), { dispatch: false });

    readonly mute$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.muteToggled),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        tap(([, state]) => { if (state.status === "active") this.signaling.setMuted(state.muted); }),
    ), { dispatch: false });

    readonly camera$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.cameraToggled),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        tap(([, state]) => { if (state.status === "active") this.signaling.setCameraDisabled(state.cameraDisabled); }),
    ), { dispatch: false });

    readonly incomingVideoProtocol$ = createEffect(() => merge(
        this.signaling.askForVideo$.pipe(map(() => CallActions.askForVideoReceived())),
        this.signaling.videoAccepted$.pipe(map(() => CallActions.remoteVideoAccepted())),
        this.signaling.videoDeclined$.pipe(map(() => CallActions.remoteVideoDeclined())),
    ));

    readonly incomingStreamProtocol$ = createEffect(() => this.signaling.remoteStreamChanged$.pipe(
        map((streamType) => CallActions.remoteStreamChanged({ streamType })),
    ));

    readonly incomingQualityProtocol$ = createEffect(() => this.signaling.qualityRequested$.pipe(
        map((quality) => CallActions.remoteQualityRequested({ quality })),
    ));

    readonly incomingRecordingProtocol$ = createEffect(() => this.signaling.remoteRecordingChanged$.pipe(
        map((recording) => CallActions.remoteRecordingChanged({ recording })),
    ));

    readonly requestVideo$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.askForVideoRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        switchMap(([, state]) => {
            if (state.status !== "active" || state.videoEnabled || state.videoRequestStatus !== "pending") return EMPTY;
            this.signaling.requestVideo();
            return timer(5000).pipe(map(() => CallActions.videoRequestCooldownEnded()));
        }),
    ));

    readonly confirmVideoRequest$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.askForVideoReceived),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([, state]) => {
            if (state.status !== "active" || state.videoEnabled) return EMPTY;
            return this.dialog.confirm({
                title: this.translate.instant("calls.videoRequest.dialogTitle"),
                message: this.translate.instant("calls.videoRequest.dialogMessage"),
                cancelLabel: this.translate.instant("calls.videoRequest.deny"),
                confirmLabel: this.translate.instant("calls.videoRequest.allow"),
                icon: "videocam",
            }, "call-dialog-video-request").pipe(
                withLatestFrom(this.store.select(callFeature.selectCallsState)),
                filter(([, current]) => current.status === "active"),
                map(([accepted]) => CallActions.videoRequestDecisionMade({ accepted })));
        }),
    ));

    readonly answerVideoRequest$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.videoRequestDecisionMade),
        exhaustMap(({ accepted }) => {
            if (!accepted) {
                this.signaling.declineVideo();
                return EMPTY;
            }
            return this.signaling.acceptVideo().pipe(
                map(() => CallActions.videoEnabled()),
                catchError(() => {
                    this.signaling.declineVideo();
                    return EMPTY;
                }),
            );
        }),
    ));

    readonly enableVideoAfterRemoteAcceptance$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.remoteVideoAccepted),
        exhaustMap(() => this.signaling.enableVideo().pipe(
            map(() => CallActions.videoEnabled()),
            catchError(() => EMPTY),
        )),
    ));

    readonly startScreenShare$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.screenShareRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([, state]) => state.status === "active" && state.videoEnabled && !state.screenSharing && !state.pointerMode && !state.drawingMode
            ? this.signaling.startScreenShare().pipe(
                map(() => CallActions.screenShareStarted()),
                catchError(() => EMPTY),
            )
            : EMPTY),
    ));

    readonly stopScreenShare$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.screenShareStopRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([, state]) => state.status === "active" && state.screenSharing
            ? this.signaling.stopScreenShare().pipe(
                map(() => CallActions.screenShareStopped()),
                catchError(() => of(CallActions.screenShareStopped())),
            )
            : EMPTY),
    ));

    readonly browserEndedScreenShare$ = createEffect(() => this.signaling.screenShareEnded$.pipe(
        tap(() => this.signaling.notifyScreenShareEnded()),
        map(() => CallActions.screenShareEndedByBrowser()),
    ));

    readonly reconcileBrowserEndedScreenShare$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.screenShareEndedByBrowser),
        map(() => CallActions.screenShareStopped()),
    ));

    readonly startPointerMode$ = createEffect(() => this.mediaCommands.pointerStream$.pipe(
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([stream, state]) => state.status === "active" && state.pointerMode && !state.screenSharing
            ? this.signaling.startPointerMode(stream).pipe(
                map(() => CallActions.pointerModeStarted()),
                catchError(() => of(CallActions.pointerModeStopped())),
            )
            : EMPTY),
    ));

    readonly stopPointerMode$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.pointerModeStopRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([, state]) => state.status === "active" && state.pointerMode
            ? this.signaling.stopPointerMode().pipe(
                map(() => CallActions.pointerModeStopped()),
                catchError(() => of(CallActions.pointerModeStopped())),
            )
            : EMPTY),
    ));

    readonly startDrawingMode$ = createEffect(() => this.mediaCommands.drawingStream$.pipe(
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([stream, state]) => state.status === "active" && state.drawingMode && !state.screenSharing && !state.pointerMode
            ? this.signaling.startDrawingMode(stream).pipe(
                map(() => CallActions.drawingModeStarted()),
                catchError(() => of(CallActions.drawingModeStopped())),
            )
            : EMPTY),
    ));

    readonly stopDrawingMode$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.drawingModeStopRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([, state]) => state.status === "active" && state.drawingMode
            ? this.signaling.stopDrawingMode().pipe(
                map(() => CallActions.drawingModeStopped()),
                catchError(() => of(CallActions.drawingModeStopped())),
            )
            : EMPTY),
    ));

    readonly saveSnapshot$ = createEffect(() => this.mediaCommands.snapshotSave$.pipe(
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([blob, state]) => {
            const user = this.session.user();
            if (state.status !== "active" || !state.drawingMode || !user) return EMPTY;
            const invitation = state.session.invitation;
            const remoteUsername = invitation.callee.username === user.username ? invitation.caller.username : invitation.callee.username;
            const upload$ = this.snapshots.upload(blob, remoteUsername).pipe(
                tap(() => this.signaling.notifySnapshotSaved()),
                concatMap(({ imageKey }) => this.snapshots.logActivity(imageKey, state.session, user).pipe(
                    catchError(() => of(null)),
                    map(() => CallActions.snapshotSaveSucceeded({ imageKey })),
                )),
                catchError(() => of(CallActions.snapshotSaveFailed())),
            );
            return concat(of(CallActions.snapshotSaveStarted()), upload$);
        }),
    ));

    readonly changeQuality$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.qualityChangeRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([{ quality }, state]) => state.status === "active" && (state.videoEnabled || quality === "audio") && !state.screenSharing && !state.pointerMode && !state.drawingMode
            ? this.signaling.changeQuality(quality, state.cameraDisabled).pipe(
                map(() => CallActions.qualityChanged({ quality })),
                catchError(() => of(CallActions.qualityChangeFailed())),
            )
            : of(CallActions.qualityChangeFailed())),
    ));

    readonly applyRemoteQuality$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.remoteQualityRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([{ quality }, state]) => state.status === "active" && (state.videoEnabled || quality === "audio") && !state.screenSharing && !state.pointerMode && !state.drawingMode
            ? this.signaling.changeQuality(quality, state.cameraDisabled).pipe(
                tap(() => this.signaling.acknowledgeQualityChange()),
                map(() => CallActions.qualityChanged({ quality })),
                catchError(() => of(CallActions.qualityChangeFailed())),
            )
            : of(CallActions.qualityChangeFailed())),
    ));

    readonly applyMediaDevices$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.mediaDevicesChangeRequested),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        exhaustMap(([{ videoDeviceId, audioDeviceId }, state]) => state.status === "active" && !state.screenSharing && !state.pointerMode && !state.drawingMode
            ? this.signaling.applyMediaDevices({ videoDeviceId, audioDeviceId }, state.quality, state.muted, state.cameraDisabled, state.videoEnabled).pipe(
                map(() => CallActions.mediaDevicesChanged()),
                catchError(() => of(CallActions.mediaDevicesChangeFailed())),
            )
            : of(CallActions.mediaDevicesChangeFailed())),
    ));

    readonly startRecording$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.recordingStartRequested),
        exhaustMap(({ source }) => from(this.recording.start(source)).pipe(
            tap(() => this.signaling.notifyRecordingStarted()),
            map(() => CallActions.recordingStarted()),
            catchError(() => of(CallActions.recordingStartFailed())),
        )),
    ));

    readonly stopRecording$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.recordingStopRequested),
        exhaustMap(() => from(this.recording.stop()).pipe(
            tap(() => this.signaling.notifyRecordingStopped()),
            map(() => CallActions.recordingReady()),
            catchError(() => of(CallActions.recordingStopFailed())),
        )),
    ));

    readonly finalizeRecordingAfterCall$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.callEnded),
        filter(() => this.recording.active),
        exhaustMap(() => from(this.recording.stop()).pipe(
            map(() => CallActions.recordingReady()),
            catchError(() => of(CallActions.recordingStopFailed())),
        )),
    ));

    readonly showRecordingResult$ = createEffect(() => this.actions$.pipe(
        ofType(CallActions.recordingReady),
        withLatestFrom(this.store.select(callFeature.selectCallsState)),
        filter(([, state]) => state.status === "active"),
        tap(() => this.materialDialog.open(RecordingResultDialogComponent, {
            id: "call-dialog-recording-result",
            panelClass: "app-dialog-panel",
            width: "640px",
            maxWidth: "calc(100vw - 24px)",
            restoreFocus: true,
        })),
    ), { dispatch: false });
}
